import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ImageList,
  ImageListItem,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import DetailModalShell from '../components/detail/DetailModalShell';
import MockupsDataTabs from '../components/detail/MockupsDataTabs';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Name', minWidth: 220, maxWidth: 520, defaultWidth: 300 },
  { key: 'type', label: 'Type', minWidth: 120, maxWidth: 260, defaultWidth: 140 },
  { key: 'external_id', label: 'Catalog UUID', minWidth: 220, maxWidth: 520, defaultWidth: 300 },
  { key: 'api_created_at', label: 'API Created', minWidth: 170, maxWidth: 340, defaultWidth: 220 },
  { key: 'updated_at', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 220 },
];

const DynamicMockupsCatalogsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [accounts, setAccounts] = React.useState([]);
  const [accountsLoading, setAccountsLoading] = React.useState(false);
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [catalogs, setCatalogs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState('');

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailCatalog, setDetailCatalog] = React.useState(null);
  const [detailTab, setDetailTab] = React.useState('mockups');
  const [catalogMockups, setCatalogMockups] = React.useState([]);
  const [loadingCatalogMockups, setLoadingCatalogMockups] = React.useState(false);
  const [catalogMockupsError, setCatalogMockupsError] = React.useState(null);

  const [etsySections, setEtsySections] = React.useState([]);
  const [loadingEtsySections, setLoadingEtsySections] = React.useState(false);
  const [etsySectionsError, setEtsySectionsError] = React.useState(null);

  const [assignedItems, setAssignedItems] = React.useState([]);
  const [loadingAssignments, setLoadingAssignments] = React.useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = React.useState([]);
  const [assigning, setAssigning] = React.useState(false);
  const [assignMessage, setAssignMessage] = React.useState(null);

  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.dynamic-mockups-catalogs',
  });

  const loadAccounts = React.useCallback(async () => {
    setAccountsLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/accounts`);
      if (!resp.ok) {
        throw new Error('Failed to load Dynamic Mockups accounts');
      }
      const payload = await resp.json();
      const nextAccounts = Array.isArray(payload.items) ? payload.items : [];
      setAccounts(nextAccounts);
      setSelectedAccountId((current) => {
        if (current && nextAccounts.some((item) => item.id === current)) {
          return current;
        }
        return nextAccounts[0]?.id || '';
      });
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, [fetchWithAuth]);

  const loadCatalogs = React.useCallback(async () => {
    if (!selectedAccountId) {
      setCatalogs([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('account_id', selectedAccountId);
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/catalogs?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load catalogs');
      }
      const payload = await resp.json();
      setCatalogs(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load catalogs');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, selectedAccountId]);

  const loadEtsySections = React.useCallback(async () => {
    setLoadingEtsySections(true);
    setEtsySectionsError(null);
    try {
      const accountsResp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/accounts`);
      if (!accountsResp.ok) {
        throw new Error('Failed to load Etsy shops');
      }
      const accountsPayload = await accountsResp.json();
      const shops = (Array.isArray(accountsPayload.items) ? accountsPayload.items : [])
        .map((item) => item.shop)
        .filter(Boolean)
        .map((shop) => ({
          id: shop.id,
          title: shop.title || shop.name || shop.external_id,
        }));

      const sectionGroups = await Promise.all(
        shops.map(async (shop) => {
          try {
            const params = new URLSearchParams({ shop_id: shop.id });
            const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/sections?${params.toString()}`);
            if (!resp.ok) {
              throw new Error('Failed to load sections');
            }
            const payload = await resp.json();
            const list = Array.isArray(payload.items) ? payload.items : [];
            return list.map((section) => ({
              ...section,
              shop_id: shop.id,
              shop_title: shop.title,
            }));
          } catch (err) {
            return [];
          }
        }),
      );

      const merged = sectionGroups
        .flat()
        .sort((a, b) => {
          const shopA = String(a.shop_title || '').toLowerCase();
          const shopB = String(b.shop_title || '').toLowerCase();
          if (shopA !== shopB) {
            return shopA.localeCompare(shopB);
          }
          return String(a.title || '').toLowerCase().localeCompare(String(b.title || '').toLowerCase());
        });

      setEtsySections(merged);
    } catch (err) {
      setEtsySectionsError(err.message || 'Failed to load Etsy sections');
      setEtsySections([]);
    } finally {
      setLoadingEtsySections(false);
    }
  }, [fetchWithAuth]);

  const loadCatalogMockups = React.useCallback(async (catalog) => {
    if (!catalog?.id) {
      setCatalogMockups([]);
      return;
    }
    setLoadingCatalogMockups(true);
    setCatalogMockupsError(null);
    try {
      const params = new URLSearchParams();
      params.set('account_id', catalog.account_id || selectedAccountId);
      params.set('catalog_id', catalog.id);
      params.set('limit', '500');
      params.set('offset', '0');
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/mockups?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load catalog mockups');
      }
      const payload = await resp.json();
      setCatalogMockups(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setCatalogMockupsError(err.message || 'Failed to load catalog mockups');
      setCatalogMockups([]);
    } finally {
      setLoadingCatalogMockups(false);
    }
  }, [fetchWithAuth, selectedAccountId]);

  const loadAssignments = React.useCallback(async (catalogID) => {
    if (!catalogID) {
      setAssignedItems([]);
      return;
    }
    setLoadingAssignments(true);
    setAssignMessage(null);
    try {
      const params = new URLSearchParams({ catalog_id: catalogID });
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/section-mockups?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load section assignments');
      }
      const payload = await resp.json();
      setAssignedItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setAssignMessage({ type: 'error', text: err.message || 'Failed to load section assignments' });
      setAssignedItems([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  React.useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  React.useEffect(() => {
    loadEtsySections();
  }, [loadEtsySections]);

  React.useEffect(() => {
    if (!detailOpen || !detailCatalog?.id) {
      return;
    }
    setDetailTab('mockups');
    setSelectedSectionIds([]);
    setAssignMessage(null);
    loadCatalogMockups(detailCatalog);
    loadAssignments(detailCatalog.id);
  }, [detailOpen, detailCatalog, loadCatalogMockups, loadAssignments]);

  const filteredCatalogs = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return catalogs;
    }
    return catalogs.filter((item) => {
      const candidates = [item.name, item.type, item.external_id];
      return candidates.some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [catalogs, search]);

  const handleOpenDetail = (catalog) => {
    setDetailCatalog(catalog);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailCatalog(null);
    setCatalogMockups([]);
    setAssignedItems([]);
    setSelectedSectionIds([]);
    setAssignMessage(null);
  };

  const assignedSectionIDs = React.useMemo(
    () => new Set(assignedItems.map((item) => item.etsy_section_id).filter(Boolean)),
    [assignedItems],
  );

  const sectionLookup = React.useMemo(() => {
    const map = new Map();
    etsySections.forEach((section) => {
      if (section.id) {
        map.set(section.id, section);
      }
    });
    return map;
  }, [etsySections]);

  const assignedSectionRows = React.useMemo(() => {
    return assignedItems
      .map((item) => item.section || sectionLookup.get(item.etsy_section_id))
      .filter(Boolean)
      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  }, [assignedItems, sectionLookup]);

  const toggleSectionSelection = (sectionID) => {
    setSelectedSectionIds((prev) =>
      prev.includes(sectionID) ? prev.filter((id) => id !== sectionID) : [...prev, sectionID],
    );
  };

  const handleAssignSections = async () => {
    if (!detailCatalog?.id || selectedSectionIds.length === 0) {
      return;
    }
    setAssigning(true);
    setAssignMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/dynamic-mockups/section-mockups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          catalog_id: detailCatalog.id,
          section_ids: selectedSectionIds,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to assign sections');
      }
      setAssignedItems(Array.isArray(payload.items) ? payload.items : []);
      const created = typeof payload.created === 'number' ? payload.created : 0;
      setSelectedSectionIds([]);
      setAssignMessage({
        type: 'success',
        text: `Assigned ${created} section${created === 1 ? '' : 's'} to this catalog.`,
      });
    } catch (err) {
      setAssignMessage({ type: 'error', text: err.message || 'Failed to assign sections' });
    } finally {
      setAssigning(false);
    }
  };

  const formattedCatalogRawData = React.useMemo(() => {
    if (!detailCatalog?.raw_data) {
      return 'No data available.';
    }
    try {
      return JSON.stringify(detailCatalog.raw_data, null, 2);
    } catch {
      return 'No data available.';
    }
  }, [detailCatalog]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="Account"
          value={selectedAccountId}
          onChange={(event) => setSelectedAccountId(event.target.value)}
          sx={{ minWidth: 240 }}
          disabled={accountsLoading || accounts.length === 0}
        >
          {accounts.length === 0 ? (
            <MenuItem value="" disabled>
              {accountsLoading ? 'Loading…' : 'No accounts'}
            </MenuItem>
          ) : (
            accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name || account.id}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search catalogs"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadCatalogs} disabled={!selectedAccountId || loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading catalogs..." />
              ) : filteredCatalogs.length === 0 ? (
                <TableEmptyRow
                  colSpan={TABLE_COLUMNS.length}
                  label={selectedAccountId ? 'No catalogs found.' : 'Select an account to view catalogs.'}
                />
              ) : (
                filteredCatalogs.map((catalog) => (
                  <TableRow
                    key={catalog.id || catalog.external_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDetail(catalog)}
                  >
                    <TableCell>{catalog.name || '—'}</TableCell>
                    <TableCell>{catalog.type || '—'}</TableCell>
                    <TableCell>{catalog.external_id || '—'}</TableCell>
                    <TableCell>{formatTimestamp(catalog.api_created_at)}</TableCell>
                    <TableCell>{formatTimestamp(catalog.updated_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DetailModalShell
        open={detailOpen}
        onClose={handleCloseDetail}
        title={detailCatalog?.name || 'Catalog Details'}
        externalId={detailCatalog?.external_id || detailCatalog?.id}
        left={
          detailCatalog ? (
            <MockupsDataTabs
              value={detailTab}
              onChange={setDetailTab}
              rawData={formattedCatalogRawData}
              mockupsToolbar={
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Mockups ({catalogMockups.length})</Typography>
                  {loadingCatalogMockups ? <CircularProgress size={18} /> : null}
                </Stack>
              }
              mockupsContent={
                <>
                  {catalogMockupsError ? <Alert severity="error" sx={{ mb: 1 }}>{catalogMockupsError}</Alert> : null}
                  {loadingCatalogMockups ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : catalogMockups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No mockups found in this catalog.</Typography>
                  ) : (
                    <ImageList cols={3} gap={8} sx={{ m: 0, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', pr: 1 }}>
                      {catalogMockups.map((mockup) => {
                        const imageURL = selectDynamicPreviewImage(mockup);
                        return (
                          <ImageListItem key={mockup.id || mockup.external_id} sx={{ position: 'relative' }}>
                            {imageURL ? (
                              <img
                                src={imageURL}
                                alt={mockup.name || mockup.external_id}
                                loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  minHeight: 140,
                                  borderRadius: 1,
                                  bgcolor: 'grey.100',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">No preview</Typography>
                              </Box>
                            )}
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 8,
                                right: 8,
                                bottom: 8,
                                bgcolor: 'rgba(0,0,0,0.62)',
                                color: '#fff',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" noWrap>
                                {mockup.name || mockup.external_id}
                              </Typography>
                            </Box>
                          </ImageListItem>
                        );
                      })}
                    </ImageList>
                  )}
                </>
              }
            />
          ) : null
        }
        right={
          detailCatalog ? (
            <>
              <InfoRow label="Name" value={detailCatalog.name || '—'} />
              <InfoRow label="Type" value={detailCatalog.type || '—'} />
              <InfoRow label="Catalog UUID" value={detailCatalog.external_id || '—'} />
              <InfoRow label="Account ID" value={detailCatalog.account_id || '—'} />
              <InfoRow label="API Created" value={formatTimestamp(detailCatalog.api_created_at)} />
              <InfoRow label="Updated" value={formatTimestamp(detailCatalog.updated_at)} />

              <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, pt: 2 }}>
                <Typography variant="subtitle1">Current section assignments</Typography>
                {loadingAssignments ? (
                  <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading assignments…</Typography>
                  </Box>
                ) : assignedSectionRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No Etsy sections assigned yet.
                  </Typography>
                ) : (
                  <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 120, overflowY: 'auto' }}>
                    {assignedSectionRows.map((section) => (
                      <Typography key={section.id} variant="body2" color="text.secondary">
                        {section.shop_title ? `${section.shop_title} · ` : ''}{section.title || section.external_id}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, pt: 2 }}>
                <Typography variant="subtitle1">Assign to Etsy sections</Typography>
                {etsySectionsError ? <Alert severity="error" sx={{ mt: 1 }}>{etsySectionsError}</Alert> : null}
                {loadingEtsySections ? (
                  <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading Etsy sections…</Typography>
                  </Box>
                ) : etsySections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No Etsy sections available.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 280, overflowY: 'auto', mt: 1, pr: 0.5 }}>
                    {etsySections.map((section) => {
                      const alreadyAssigned = assignedSectionIDs.has(section.id);
                      return (
                        <FormControlLabel
                          key={section.id}
                          control={
                            <Checkbox
                              checked={selectedSectionIds.includes(section.id)}
                              onChange={() => toggleSectionSelection(section.id)}
                              disabled={alreadyAssigned}
                            />
                          }
                          label={`${section.shop_title || 'Shop'} · ${section.title || section.external_id}${alreadyAssigned ? ' (Assigned)' : ''}`}
                        />
                      );
                    })}
                  </Box>
                )}

                {assignMessage ? (
                  <Alert severity={assignMessage.type} sx={{ mt: 1 }} onClose={() => setAssignMessage(null)}>
                    {assignMessage.text}
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleAssignSections}
                    disabled={assigning || selectedSectionIds.length === 0 || !detailCatalog?.id}
                  >
                    {assigning ? 'Assigning…' : `Assign Selected (${selectedSectionIds.length})`}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setSelectedSectionIds([])}
                    disabled={assigning || selectedSectionIds.length === 0}
                  >
                    Clear
                  </Button>
                </Stack>
              </Box>
            </>
          ) : null
        }
      />
    </Box>
  );
};

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '70%', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Stack>
);

function selectDynamicPreviewImage(mockup) {
  if (!mockup) {
    return '';
  }
  const candidates = [mockup.preview_url, mockup.thumbnail_url, mockup.render_url, mockup.source_url];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim();
    }
  }
  return '';
}

function formatTimestamp(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

export default DynamicMockupsCatalogsPage;
